import { Theme, Typography } from '@mui/material';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles((theme: Theme) => ({
  text: {
    '&.inline': {
      display: 'inline-block',
    },
    '&.fixLineHeight': {
      lineHeight: '100%',
    },
    '&.bold': {
      fontWeight: theme.typography.fontWeightBold,
    },
    '&.italic': {
      fontStyle: 'italic',
    },
    '&.black': {
      color: theme.palette.common.black,
    },
    '&.white': {
      color: theme.palette.common.white,
    },
    '&.grey': {
      color: theme.palette.darkGray,
    },
    '&.light-grey': {
      color: theme.palette.gray,
    },
    '&.active': {
      color: theme.palette.secondary.main,
    },
    '&.success': {
      color: theme.palette.success.main,
    },
    '&.link': {
      color: theme.palette.link.main,
      '&:hover': {
        color: theme.palette.link.hover,
      },
    },
    '&.info': {
      color: theme.palette.info.main,
    },
    '&.error': {
      color: theme.palette.error.main,
    },
    fontSize: theme.typography.body1.fontSize,
    '&.x-small': {
      fontSize: theme.typography.caption.fontSize,
    },
    '&.small': {
      fontSize: theme.typography.body2.fontSize,
    },
    '&.large': {
      fontSize: theme.typography.h6.fontSize,
    },
    '&.x-large': {
      fontSize: theme.typography.h5.fontSize,
    },
    '&.dotdot': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

interface Props {
  children: ReactNode;
  noWrap?: boolean;
  size?: 'x-large' | 'large' | 'medium' | 'small' | 'x-small';
  bold?: boolean;
  italic?: boolean;
  color?:
    | 'grey'
    | 'black'
    | 'white'
    | 'active'
    | 'success'
    | 'link'
    | 'info'
    | 'error'
    | 'inherit'
    | 'light-grey';
  dotdot?: boolean;
  inline?: boolean;
  className?: string;
  fixLineHeight?: boolean;
}

const Text: FC<Props> = ({
  size = 'medium',
  children,
  noWrap = false,
  bold = false,
  italic = false,
  color = 'inherit',
  dotdot = false,
  inline = false,
  className = '',
  fixLineHeight = false,
}) => {
  const styles = useStyles();
  const component = size === 'x-large' ? 'h1' : 'div';
  const compClass = classNames(styles.text, size, color, className, {
    bold,
    dotdot,
    italic,
    inline,
    fixLineHeight,
  });

  return (
    <Typography component={component} className={compClass} color="inherit" noWrap={noWrap}>
      {children}
    </Typography>
  );
};

export default Text;
